import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Star,
  ShieldCheck,
  Award,
  Hammer,
  MapPin,
  Compass,
  HeartHandshake,
  MessageCircle,
  Layers,
  ClipboardCheck,
  CalendarCheck,
  Gem,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import PhotoPlaceholder from "../components/PhotoPlaceholder";
import Seo from "../components/Seo";
import { MaskedWords, FadeIn, Reveal } from "../components/Motion";
import { track } from "../lib/track";
import {
  SERVICES,
  BUSINESS,
  WHY_CHOOSE,
  FEATURED_AREAS,
  ADDITIONAL_SERVICE_AREAS,
  FAMILY_OWNED,
  CRAFTSMANSHIP,
} from "../data/site";

const WHY_ICONS = {
  "Quality Craftsmanship": Hammer,
  "Premium Materials": Gem,
  "Composite Deck Expertise": Award,
  "Permit Assistance": ClipboardCheck,
  "Reliable Scheduling": CalendarCheck,
  "Honest Pricing": HeartHandshake,
  "Excellent Communication": MessageCircle,
};

const FAMILY_ICONS = [Compass, Hammer, Gem, MapPin, MessageCircle];

const HERO_DEFAULTS = {
  image: "/hero-deck.webp",
  overline: "Family-Owned • Rome, Cartersville & Northwest Georgia",
  headline: "Custom Decks Built to Last.",
  subheadline:
    "Premium composite, wood, elevated and covered decks serving Rome, Cartersville, Metro Atlanta and surrounding North Georgia communities.",
  cta_primary_label: "Get Your Free Estimate",
  cta_primary_href: "/contact",
  cta_secondary_label: "View Our Work",
  cta_secondary_href: "/gallery",
};

export default function Home() {
  const [hero, setHero] = useState(HERO_DEFAULTS);
  const [reviews, setReviews] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [financing, setFinancing] = useState(null);
  const [projects, setProjects] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api/content`;
    fetch(`${API}/hero`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setHero({ ...HERO_DEFAULTS, ...d }))
      .catch(() => {});
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setReviews(d))
      .catch(() => {});
    fetch(`${API}/home_faq`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && Array.isArray(d.items) && setFaqs(d.items))
      .catch(() => {});
    fetch(`${API}/financing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setFinancing(d))
      .catch(() => {});
    fetch(`${API}/projects`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && Array.isArray(d.items) && setProjects(d.items))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="Zarco's Decks — Custom Decks Built to Last | Rome, Cartersville & North Georgia"
        description="Family-owned deck builder serving Rome, Cartersville, Metro Atlanta and North Georgia. TimberTech composite & PVC decks, custom wood decks, covered porches, outdoor living. Free on-site estimate."
      />
      {/* HERO — dynamic image + copy from CMS with local fallback; kinetic masked reveal + parallax */}
      <section
        ref={heroRef}
        className="relative min-h-[88vh] flex items-end pt-32 pb-16 md:pb-24 bg-[#111] overflow-hidden"
      >
        <motion.div
          style={{ y: heroImgY, scale: heroImgScale }}
          className="absolute inset-0 will-change-transform"
        >
          <img
            src={hero.image}
            srcSet={hero.image === "/hero-deck.webp"
              ? "/hero-deck-768.webp 768w, /hero-deck-1280.webp 1280w, /hero-deck-1920.webp 1920w"
              : undefined}
            sizes="100vw"
            alt="Zarco's Decks & Outdoor Living — signature project"
            data-testid="hero-image"
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 pointer-events-none" />
        <div className="absolute inset-0 bg-black/25 pointer-events-none" />

        {/* Official TimberTech trust mark — desktop: upper-right of hero (white variant on dark photo) */}
        <FadeIn
          delay={0.5}
          className="hidden lg:block absolute top-28 right-12 lg:right-20 z-10"
        >
          <div data-testid="hero-timbertech-mark" className="bg-white px-6 py-4 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/40">
            <img
              src="/brand/timbertech-logo.svg"
              alt="TimberTech — premium decking brand used by Zarco's Decks"
              className="w-[160px] h-auto"
              loading="eager"
            />
          </div>
        </FadeIn>

        <div className="relative px-6 md:px-12 lg:px-20 max-w-5xl">
          <FadeIn delay={0.1} y={15} duration={0.6}>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#E8D6C2] font-semibold">
              {hero.overline}
            </p>
          </FadeIn>
          <h1
            data-testid="hero-title"
            className="font-display mt-6 text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter font-medium text-white leading-[0.95]"
          >
            <MaskedWords text={hero.headline} />
          </h1>
          <FadeIn delay={0.45} y={20} duration={0.7}>
            <p
              data-testid="hero-subtitle"
              className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed"
            >
              {hero.subheadline}
            </p>
          </FadeIn>

          <FadeIn delay={0.65} y={20} duration={0.6}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={hero.cta_primary_href || "/contact"}
                data-testid="hero-free-estimate-btn"
                className="group bg-white text-[#111] hover:bg-[#C27A3E] hover:text-white transition-colors px-8 py-4 font-semibold tracking-wide flex items-center gap-3"
              >
                {hero.cta_primary_label}{" "}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to={hero.cta_secondary_href || "/gallery"}
                data-testid="hero-gallery-btn"
                className="border-2 border-white/70 text-white hover:bg-white hover:text-[#111] transition-colors px-8 py-4 font-semibold tracking-wide"
              >
                {hero.cta_secondary_label}
              </Link>
            </div>
          </FadeIn>

          {/* Official TimberTech trust mark — mobile: below hero CTAs, clear of nav/headline */}
          <FadeIn delay={0.8} y={15} duration={0.6} className="lg:hidden">
            <div data-testid="hero-timbertech-mark-mobile" className="mt-8 inline-block bg-white px-5 py-3 shadow-[0_16px_40px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/40">
              <img
                src="/brand/timbertech-logo.svg"
                alt="TimberTech — premium decking brand used by Zarco's Decks"
                className="w-[140px] h-auto"
                loading="eager"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TRUST BAR — lightened: white with charcoal text and wood accents */}
      <section className="bg-white border-b border-[#E7E2D9] py-5 overflow-hidden">
        <div className="flex whitespace-nowrap zd-marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex shrink-0 items-center">
              {[
                { icon: ShieldCheck, label: "Insured • Family-Owned & Operated" },
                { icon: Hammer, label: "Family-Owned & Operated" },
                { icon: Award, label: "Composite Deck Specialists" },
                { icon: Star, label: "Premium Materials" },
                { icon: MapPin, label: "Serving Metro Atlanta & North GA" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-3 mx-8 text-sm tracking-wide text-[#2A2A2A]">
                  <Icon size={18} strokeWidth={1.5} className="text-[#99582A]" />
                  {label}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* FAMILY-OWNED & OPERATED */}
      <section
        data-testid="family-owned-section"
        className="py-24 md:py-36 px-6 md:px-12 lg:px-20 bg-white"
      >
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              {FAMILY_OWNED.overline}
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium mt-5 text-[#111] leading-[1.03]">
              {FAMILY_OWNED.title}
            </h2>
            <p className="mt-7 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              {FAMILY_OWNED.subtitle}
            </p>
            <Link
              to="/contact"
              data-testid="family-free-estimate-btn"
              className="inline-flex items-center gap-3 mt-10 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-7 py-4 font-semibold tracking-wide"
            >
              Get Your Free Estimate <ArrowRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-7 space-y-4 md:space-y-5">
            {FAMILY_OWNED.pillars.map((p, i) => {
              const Icon = FAMILY_ICONS[i] || Compass;
              return (
                <Reveal
                  key={p.title}
                  delay={i * 0.08}
                  data-testid={`family-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="zd-card-lift bg-white border border-[#E7E2D9] p-7 md:p-8 flex gap-6"
                >
                  <div className="shrink-0 w-12 h-12 bg-[#FBF8F3] border border-[#E7E2D9] flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} className="text-[#99582A]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-4">
                      <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium text-[#111]">
                        {p.title}
                      </h3>
                      <span className="text-[11px] tracking-[0.25em] font-semibold text-[#99582A]/70 whitespace-nowrap">
                        {String(i + 1).padStart(2, "0")} // {String(FAMILY_OWNED.pillars.length).padStart(2, "0")}
                      </span>
                    </div>
                    <p className="text-sm md:text-base text-[#4A4A4A] mt-2 leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY HOMEOWNERS CHOOSE — bright cream */}
      <section
        data-testid="why-choose-section"
        className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3]"
      >
        <SectionHeading
          overline="Why Homeowners Choose Zarco's Decks"
          title="Seven reasons clients invest in Zarco's for their outdoor living build."
          subtitle="From the first walk-through to the final inspection, every choice we make is built around longevity, craftsmanship, and clear communication."
        />

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {WHY_CHOOSE.map((w, i) => {
            const Icon = WHY_ICONS[w.title] || Layers;
            return (
              <Reveal
                key={w.title}
                delay={(i % 4) * 0.08}
                data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="zd-card-lift group bg-white border border-[#E7E2D9] p-8"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-3xl tracking-tight font-medium text-[#99582A]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="w-12 h-12 bg-[#FBF8F3] border border-[#E7E2D9] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-6 group-hover:scale-105">
                    <Icon size={20} strokeWidth={1.5} className="text-[#99582A]" />
                  </div>
                </div>
                <h3 className="font-display text-xl tracking-tight font-medium mt-6 text-[#111]">
                  {w.title}
                </h3>
                <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">
                  {w.body}
                </p>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-14 flex justify-center">
          <Link
            to="/contact"
            data-testid="why-cta-free-estimate-btn"
            className="bg-[#111] text-white hover:bg-[#99582A] transition-colors px-9 py-4 font-semibold tracking-wide inline-flex items-center gap-3"
          >
            Get Your Free Estimate <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* SERVICES TEASER — white, bigger cards */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="What We Build"
              title="Outdoor living designed for Metro Atlanta homes."
              subtitle="Custom, composite, and full outdoor living spaces — designed for the homes and lifestyles of Marietta, Alpharetta, Kennesaw, Roswell, Woodstock, and surrounding communities."
            />
            <Link
              to="/services"
              data-testid="home-services-link"
              className="inline-flex items-center gap-2 mt-8 font-semibold border-b-2 border-[#111] pb-1 hover:text-[#99582A] hover:border-[#99582A]"
            >
              All services <ArrowRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {SERVICES.slice(0, 4).map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.08} className="h-full">
                <Link
                  to="/services"
                  data-testid={`home-service-card-${s.slug}`}
                  className="zd-card-lift group block bg-white border border-[#E7E2D9] h-full"
                >
                  {s.photo ? (
                    <div className="img-hover img-spotlight aspect-[4/3]">
                      <img
                        src={s.photo}
                        alt={s.imageLabel || s.title}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: s.focal || "center" }}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <PhotoPlaceholder
                      label={s.imageLabel || s.title}
                      sublabel="Add real project photo"
                      aspect="4/3"
                      variant="dark"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="font-display text-xl tracking-tight font-medium text-[#111] transition-colors group-hover:text-[#99582A]">
                      {s.title}
                    </h3>
                    <p className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{s.short}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT WORK — brightened (cream instead of black) */}
      <section className="bg-[#FBF8F3] py-24 md:py-32 px-6 md:px-12 lg:px-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Recent Work
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
              Recent projects — Northwest Georgia.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/gallery"
              data-testid="home-gallery-link"
              className="inline-flex items-center gap-2 font-semibold border-b-2 border-[#111] pb-1 hover:text-[#99582A] hover:border-[#99582A]"
            >
              See the full gallery <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { label: "Elevated Custom Deck", src: "/project-elevated-deck-01.webp", focal: "center 55%" },
            { label: "Covered Composite Porch", src: "/project-covered-porch-01.webp", focal: "center 40%" },
            { label: "Screened Porch Build", src: "/project-screened-porch-01.webp", focal: "center 45%" },
            { label: "Two-Story Porch Elevation", src: "/project-covered-porch-elevation.webp", focal: "center 35%" },
          ].map((tile, i) => (
            <Reveal
              key={tile.src}
              delay={i * 0.08}
              y={24}
              data-testid={`recent-work-${i}`}
              className={`img-hover img-spotlight relative overflow-hidden ${
                i === 0 ? "col-span-2 md:col-span-2 md:row-span-2 aspect-[4/5] md:aspect-[4/5]" : "aspect-square"
              }`}
            >
              <img
                src={tile.src}
                alt={tile.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: tile.focal }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <span className="absolute bottom-3 left-3 md:bottom-5 md:left-5 text-white text-[11px] md:text-sm font-semibold tracking-wide">
                {tile.label}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CRAFTSMANSHIP — honest build-in-progress feature */}
      <section
        data-testid="craftsmanship-section"
        className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white"
      >
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              {CRAFTSMANSHIP.overline}
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium mt-5 text-[#111] leading-[1.02]">
              {CRAFTSMANSHIP.title}
            </h2>
            <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-xl">
              {CRAFTSMANSHIP.body}
            </p>
            <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">
              Build in progress · Real jobsite
            </p>
            <Link
              to="/contact"
              data-testid="craftsmanship-free-estimate-btn"
              className="inline-flex items-center gap-3 mt-8 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide"
            >
              Get Your Free Estimate <ArrowRight size={18} />
            </Link>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5 order-1 lg:order-2">
            <div className="img-hover img-spotlight aspect-[4/5] w-full">
              <img
                src={CRAFTSMANSHIP.image}
                alt={CRAFTSMANSHIP.caption}
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ objectPosition: CRAFTSMANSHIP.focal }}
              />
            </div>
            <p className="mt-3 text-xs text-[#666] italic">{CRAFTSMANSHIP.caption}</p>
          </Reveal>
        </div>
      </section>

      {/* SEO BLOCK — white */}
      <section className="py-20 md:py-24 px-6 md:px-12 lg:px-20 bg-white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Local. Premium. Proven.
            </p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-4 text-[#111]">
              The deck builder Metro Atlanta trusts.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            <p>
              Searching for a{" "}
              <strong className="text-[#111]">Deck Builder in Marietta, Kennesaw, or Alpharetta, GA</strong>?
              Zarco's Decks designs and builds custom decks, TimberTech
              composite & PVC installations, covered porches, and full outdoor
              living spaces throughout Metro Atlanta and North Georgia.
            </p>
            <p>
              As a trusted{" "}
              <strong className="text-[#111]">TimberTech Deck Builder in Alpharetta GA</strong> and across
              Roswell, Woodstock, Milton, Suwanee, Canton, Cartersville, and
              Acworth, we deliver fade-resistant, low-maintenance TimberTech
              composite & PVC builds backed by manufacturer warranties — with
              other quality options like Trex available to fit your project —
              alongside hand-crafted cedar, pressure-treated, and hardwood decks
              built for our climate.
            </p>
            <p>
              From new construction to detailed repairs, every{" "}
              <strong className="text-[#111]">Custom Deck Builder Georgia</strong> project we
              deliver follows the same Zarco's process: on-site walk-through,
              transparent quote, clean build, and a workmanship warranty.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — white, 5-step editorial process */}
      <section data-testid="how-it-works-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white border-t border-[#E7E2D9]">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              How It Works
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
              A straightforward process from estimate to final walkthrough.
            </h2>
            <Link
              to="/contact"
              data-testid="how-it-works-cta"
              className="group inline-flex items-center gap-3 mt-8 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-8 py-4 font-semibold tracking-wide"
            >
              Start with a Free Estimate{" "}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
          <div className="lg:col-span-8">
            {[
              { n: "01", t: "Consult", d: "We learn about your project, your goals, your property, and the type of outdoor space you want to create — starting with a free on-site estimate." },
              { n: "02", t: "Design & Material Selection", d: "We help you compare layout, decking, railing, stairs, covered options, and material choices such as TimberTech." },
              { n: "03", t: "Project Planning", d: "You receive a clear written scope with the project details, selections, and a realistic schedule discussed during the estimate." },
              { n: "04", t: "Build", d: "Once the project is approved and ready to begin, our team moves into construction with attention to structure, craftsmanship, and jobsite communication." },
              { n: "05", t: "Final Walkthrough", d: "We walk the completed project with you, review the finished work, and make sure you understand any applicable care or manufacturer warranty information." },
            ].map((s, i) => (
              <Reveal
                key={s.n}
                delay={i * 0.06}
                y={20}
                data-testid={`how-step-${s.n}`}
                className="grid grid-cols-12 gap-5 md:gap-8 border-b border-[#E7E2D9] py-7 first:pt-0 last:border-b-0"
              >
                <p className="col-span-2 md:col-span-1 font-display text-3xl tracking-tight font-medium text-[#99582A]">
                  {s.n}
                </p>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium text-[#111]">
                    {s.t}
                  </h3>
                  <p className="mt-2 text-sm md:text-base text-[#4A4A4A] leading-relaxed max-w-2xl">
                    {s.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS TRUST STRIP — official TimberTech brand mark; no unauthorized logos */}
      <section data-testid="materials-trust-strip" className="py-20 md:py-24 px-6 md:px-12 lg:px-20 bg-[#FBF8F3] border-t border-[#E7E2D9]">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Materials & Systems We Build With
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
              Premium materials. Built the right way.
            </h2>
            <p className="mt-5 text-[#4A4A4A] leading-relaxed max-w-2xl">
              Zarco's Decks & Outdoor Living builds with TimberTech composite and Advanced PVC
              decking and railing systems as our primary premium low-maintenance line — alongside
              pressure-treated lumber and select outdoor-living systems chosen for each project's
              design, budget, and performance requirements.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-5 flex lg:justify-end">
            <div className="bg-white border border-[#E7E2D9] px-10 py-8 shadow-[0_24px_60px_-30px_rgba(17,17,17,0.15)]">
              <img
                src="/brand/timbertech-logo.svg"
                alt="TimberTech — official brand logo"
                data-testid="trust-strip-timbertech-logo"
                className="w-[190px] md:w-[220px] h-auto"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ — CMS-editable (content key home_faq) */}
      {faqs.length > 0 && (
        <section data-testid="home-faq-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-white border-t border-[#E7E2D9]">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Frequently Asked Questions
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111] max-w-3xl">
              Honest answers before you commit.
            </h2>
          </Reveal>
          <div className="mt-12 max-w-4xl divide-y divide-[#E7E2D9] border-t border-b border-[#E7E2D9]">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.04} y={16}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`home-faq-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                >
                  <span className="font-display text-lg md:text-xl tracking-tight font-medium text-[#111] group-hover:text-[#99582A] transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-[#99582A] transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-500 ease-out ${openFaq === i ? "max-h-[1000px]" : "max-h-0"}`}
                >
                  <p data-testid={`home-faq-answer-${i}`} className="pb-2 text-[#4A4A4A] leading-relaxed max-w-3xl">{f.a}</p>
                  {f.note && (
                    <p data-testid={`home-faq-note-${i}`} className="text-xs text-[#999] italic max-w-3xl">{f.note}</p>
                  )}
                  {/* financing CTA switches automatically once a provider is activated in the CMS */}
                  {f.cta_kind === "financing" && (
                    financing?.provider_active && financing?.provider_url ? (
                      <a
                        href={financing.provider_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`home-faq-cta-${i}`}
                        onClick={() => { track("financing_provider_click", { provider: financing.provider_name, location: "home_faq" }); track("provider_financing_click", { provider: financing.provider_name, location: "home_faq" }); }}
                        className="mt-4 mb-6 inline-flex items-center gap-2 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-6 py-3 text-sm font-semibold tracking-wide"
                      >
                        Check Financing Options <ExternalLink size={14} />
                      </a>
                    ) : (
                      <Link
                        to="/contact?financing=1"
                        data-testid={`home-faq-cta-${i}`}
                        onClick={() => track("financing_interest_click", { location: "home_faq" })}
                        className="mt-4 mb-6 inline-flex items-center gap-2 bg-[#111] text-white hover:bg-[#99582A] transition-colors px-6 py-3 text-sm font-semibold tracking-wide"
                      >
                        I'm Interested in Financing <ArrowRight size={14} />
                      </Link>
                    )
                  )}
                  {f.cta_href && (
                    <Link
                      to={f.cta_href}
                      data-testid={`home-faq-cta-${i}`}
                      className="mt-4 mb-6 inline-flex items-center gap-2 border-2 border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors px-6 py-3 text-sm font-semibold tracking-wide"
                    >
                      {f.cta_label} <ArrowRight size={14} />
                    </Link>
                  )}
                  <div className="pb-6" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS — CMS-driven case-study cards; hidden until real projects are added */}
      {projects.length > 0 && (
        <section data-testid="featured-projects-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3] border-t border-[#E7E2D9]">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
              Project Stories
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
              Real projects. Real details.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <Reveal key={p.id || i} delay={i * 0.08} className="h-full">
                <Link
                  to={`/projects/${p.id || p.slug}`}
                  data-testid={`featured-project-${p.id || i}`}
                  className="zd-card-lift group h-full bg-white border border-[#E7E2D9] hover:border-[#99582A] transition-colors flex flex-col"
                >
                  {Array.isArray(p.photos) && p.photos[0] && (
                    <div className="img-hover img-spotlight aspect-[16/10] bg-[#E7E2D9]">
                      <img src={p.photos[0]} alt={p.title || "Zarco's Decks project"} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-7 flex flex-col flex-1">
                    {p.city && (
                      <p className="text-[11px] uppercase tracking-[0.25em] text-[#99582A] font-semibold">{p.city}</p>
                    )}
                    <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium mt-2 text-[#111] group-hover:text-[#99582A] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-xs uppercase tracking-[0.15em] text-[#666]">
                      {[p.project_type, p.material, p.railing].filter(Boolean).join(" · ")}
                    </p>
                    {p.description && (
                      <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed flex-1">{p.description}</p>
                    )}
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#99582A]">
                      View Project <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* REVIEWS — CMS-gated: renders only when real Google reviews are connected */}
      {reviews?.enabled && (reviews.gbp_url || (Array.isArray(reviews.items) && reviews.items.length > 0)) && (
        <section data-testid="reviews-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#FBF8F3]">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <Reveal className="lg:col-span-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
                Customer Reviews
              </p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
                {reviews.heading || "See What Homeowners Say"}
              </h2>
            </Reveal>
            {reviews.gbp_url && (
              <Reveal delay={0.08} className="lg:col-span-4 lg:text-right">
                <a
                  href={reviews.gbp_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="reviews-google-cta"
                  className="group inline-flex items-center gap-3 border-2 border-[#111] hover:bg-[#111] hover:text-white transition-colors px-7 py-4 font-semibold tracking-wide"
                >
                  Read Our Reviews on Google{" "}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </a>
              </Reveal>
            )}
          </div>
          {Array.isArray(reviews.items) && reviews.items.length > 0 && (
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {reviews.items.map((r, i) => (
                <Reveal key={i} delay={i * 0.08} data-testid={`review-card-${i}`} className="zd-card-lift bg-white border border-[#E7E2D9] p-8 flex flex-col">
                  {r.rating > 0 && (
                    <div className="flex gap-1 text-[#C27A3E]">
                      {[...Array(Math.min(5, r.rating))].map((_, k) => (
                        <Star key={k} size={16} fill="#C27A3E" strokeWidth={0} />
                      ))}
                    </div>
                  )}
                  <p className="font-display text-lg mt-5 leading-snug tracking-tight text-[#111] flex-1">
                    "{r.quote}"
                  </p>
                  <p className="mt-6 text-sm text-[#4A4A4A]">{r.name}{r.location ? ` — ${r.location}` : ""}</p>
                </Reveal>
              ))}
            </div>
          )}
        </section>
      )}

      {/* SERVICE AREAS */}
      <section className="bg-[#FBF8F3] py-20 md:py-28 px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
            Where We Build
          </p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight font-medium mt-4 text-[#111]">
            Serving Rome, Cartersville & Northwest Georgia.
          </h2>
          <p className="mt-5 text-[#4A4A4A] leading-relaxed text-base md:text-lg">
            Zarco's Decks is based in Northwest Georgia, building premium
            outdoor living spaces for Rome, Cartersville and the surrounding
            communities — reaching into Metro Atlanta and across North Georgia.
          </p>
          <p className="mt-4 text-sm text-[#666] leading-relaxed">
            Serving homeowners across Rome and select North Georgia / Metro
            Atlanta communities.
          </p>
        </div>

        {/* Featured markets — highest prominence */}
        <Reveal
          data-testid="featured-service-areas"
          className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          {FEATURED_AREAS.map((area) => (
            <div
              key={area}
              data-testid={`featured-area-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
              className="zd-card-lift bg-[#111] text-white p-6 flex flex-col justify-between min-h-[120px]"
            >
              <MapPin size={20} strokeWidth={1.75} className="text-[#C27A3E]" />
              <span className="font-display text-lg md:text-xl tracking-tight font-medium mt-6">
                {area}
              </span>
            </div>
          ))}
        </Reveal>

        {/* Additional communities served */}
        <div className="mt-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#99582A] font-semibold">
            Also Serving These Communities
          </p>
          <ul
            data-testid="additional-service-areas"
            className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-3"
          >
            {ADDITIONAL_SERVICE_AREAS.map((city) => (
              <li
                key={city}
                className="flex items-center gap-2.5 text-[#111] text-sm md:text-base border-b border-[#E7E2D9] pb-3"
              >
                <MapPin size={15} strokeWidth={1.75} className="text-[#99582A] shrink-0" />
                {city}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-[#4A4A4A] leading-relaxed">
            Don't see your town? We serve communities all across Northwest
            Georgia and Metro Atlanta — just ask.
          </p>
        </div>
      </section>

      {/* CTA STRIP — charcoal (lighter than pure black) with wood CTA */}
      <section className="bg-[#2A2A2A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-24">
        <Reveal className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E8D6C2] font-semibold">
              Free, no-obligation quote
            </p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4">
              Ready to start your project?
            </h2>
            <p className="mt-4 text-white/85 max-w-xl">
              Call{" "}
              <a
                href={BUSINESS.phoneMainHref}
                data-testid="home-cta-phone"
                className="underline underline-offset-4 decoration-white/60 hover:decoration-white"
              >
                {BUSINESS.phoneMain}
              </a>{" "}
              or request an estimate online — we'll respond within 24 hours.
            </p>
          </div>
          <div className="flex flex-col items-start gap-4">
            <Link
              to="/contact"
              data-testid="home-cta-free-estimate-btn"
              className="bg-[#C27A3E] text-white hover:bg-white hover:text-[#111] transition-colors px-10 py-5 font-semibold tracking-wide flex items-center gap-3"
            >
              Get Your Free Estimate <ArrowRight size={18} />
            </Link>
            <Link
              to="/financing"
              data-testid="home-financing-link"
              className="text-sm text-white/70 hover:text-white underline underline-offset-4 decoration-white/40 hover:decoration-white transition-colors"
            >
              Planning a larger project? Ask us about financing options.
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
